/**
 * ============================================================
 * 屏幕自适应插件（PC / Pad / Mobile 三端）
 * ------------------------------------------------------------
 * 机制：
 *   以 1920px 为设计稿宽度，对 <html> 设置 zoom 等比缩放，
 *   缩放区间 [0.66, 1.5]，一次覆盖 1280 / 1366 / 1440 / 1536 /
 *   1600 / 1920 / 2560 等主流分辨率，Element UI 组件、弹窗、
 *   echarts 同步缩放，视觉比例一致，无横向滚动条。
 *
 * 与旧机制的合并说明：
 *   1) 原 src/plugins/devicePixelRatio.js（body zoom = 1/dpr）的
 *      逻辑并入本文件：clientWidth 为 CSS 视口宽，天然已包含
 *      Windows 系统缩放（devicePixelRatio）修正，无需再叠加
 *      zoom 以避免双重缩小；同时为 <html> 标记 data-dpr 供
 *      样式按需微调（如 1px 边框、高分屏细节）。
 *   2) 原 src/plugins/rem.js 动态根字号停用（固定 16px），
 *      页面中 rem / px 统一由本文件的 zoom 缩放，避免双重缩放。
 *
 * 断点（步骤 9：Pad / 手机端自适应）：
 *   html[data-bp="pc"]     >= 1280  保持等比缩放，视觉一致
 *   html[data-bp="pad"]    768~1279 关闭等比缩放（zoom=1），
 *                                   切换移动化布局（侧边栏抽屉、
 *                                   内容留白收窄、表格横向滚动）
 *   html[data-bp="mobile"] < 768    关闭等比缩放（zoom=1），
 *                                   全移动化样式（弹窗全屏化、
 *                                   操作按钮折叠、顶栏精简等）
 *
 *   断点变化时向 window 派发 'adaptive-bpchange' 事件，
 *   供 Home.vue 等框架组件同步抽屉/顶栏交互状态。
 * ============================================================
 */

const DESIGN_WIDTH = 1920 // 设计稿宽度
const MIN_SCALE = 0.66    // 最小缩放（1280 / 1920 ≈ 0.667）
const MAX_SCALE = 1.5     // 最大缩放（2560 / 1920 ≈ 1.333）
const BP_PC = 1280        // PC 断点（>= 1280 走等比缩放）
const BP_MOBILE = 768     // Mobile 断点（< 768 全移动化）

let scale = 1
let breakpoint = 'pc'

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max)
}

/** 当前缩放比例 */
export function getScale() {
  return scale
}

/** 当前断点：pc | pad | mobile */
export function getBreakpoint() {
  return breakpoint
}

/** 应用一次自适应缩放 */
export function applyAdaptive() {
  const html = document.documentElement
  const width = html.clientWidth || window.innerWidth
  const dpr = window.devicePixelRatio || 1
  const prevBp = html.getAttribute('data-bp')

  if (width >= BP_PC) {
    // PC：1920 设计稿等比缩放
    scale = clamp(width / DESIGN_WIDTH, MIN_SCALE, MAX_SCALE)
    breakpoint = 'pc'
  } else {
    // Pad / Mobile：关闭等比缩放（zoom=1），
    // 依赖 CSS 断点样式（html[data-bp=...]）做移动化布局
    scale = 1
    breakpoint = width < BP_MOBILE ? 'mobile' : 'pad'
  }

  html.style.zoom = scale.toFixed(4)
  html.setAttribute('data-dpr', String(dpr))
  html.setAttribute('data-bp', breakpoint)

  // 断点切换时通知框架组件（Home.vue 抽屉/顶栏）
  if (prevBp !== breakpoint) {
    window.dispatchEvent(new Event('adaptive-bpchange'))
  }
}

let rafId = null
function onResize() {
  if (rafId) window.cancelAnimationFrame(rafId)
  rafId = window.requestAnimationFrame(applyAdaptive)
}

/** 初始化（入口最先 import 本文件，模块加载即生效，避免首屏闪烁） */
export function initAdaptive() {
  applyAdaptive()
  window.addEventListener('resize', onResize)
  window.__ADAPTIVE__ = { getScale, getBreakpoint }
}

if (typeof window !== 'undefined') {
  initAdaptive()
}

export default { initAdaptive, applyAdaptive, getScale, getBreakpoint }
